import { useRef, useEffect, useCallback, memo, useState } from 'react'
import { observer } from 'mobx-react'

import { useStores } from 'stores/utils/hooks/useStores'
import { useField } from 'react-final-form'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useInteraction } from 'hooks/useInteraction'
import { usePayoutAccountValidation } from 'hooks/usePayoutAccountValidation'
import { isTouchIgnoredConfig } from 'config/utils/config'
import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { getCookie, COOKIE_KEY_FCID } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import { useData } from 'stores/utils/hooks/useData'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { logInfo } from 'utils/log'

export enum InactivityType {
  PAYOUT_ACCOUNT_INACTIVITY,
  DAC_INACTIVITY
}

interface IdleDetectorProps {
  onNext: () => void
  timeout: number
  inactivityType?: InactivityType
}

const IBAN_PATH = 'common.payoutAccount.iban'
const ACCOUNT_NUMBER_PATH = 'common.payoutAccount.accountNumber'
const BANKCODE_PATH = 'common.payoutAccount.bankCode'

const CreateFinalAfterInactivity = observer(
  ({ onNext, timeout, inactivityType }: IdleDetectorProps) => {
    const {
      page: { isLastDisplayedPage, config },
      navigation: { currentPageInSession },
      fieldErrors: { isErrorOnFinalize },
      traversal: { dacWidgetState }
    } = useStores()
    const { hasInteracted, setHasInteracted } = useInteraction()
    const isLastPage = isLastDisplayedPage(currentPageInSession)
    const { data } = useData()

    const ibanField = useField(IBAN_PATH, {})
    const accountNumberField = useField(ACCOUNT_NUMBER_PATH, {})
    const bankCodeField = useField(BANKCODE_PATH, {})
    const ageGroup = useAgeGroup()
    const timeoutRef = useRef<number | undefined>(undefined)
    const [isTimeoutStarted, setIsTimeoutStarted] = useState(false)

    const isTouchedIgnored = isTouchIgnoredConfig(config.name)

    const createFinalAfterInactivityAfterDacChoice = useCallback(() => {
      trackInAmplitude(AmplitudeEvent.FinalAfterInactivityAfterDACChoice)
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: `${AmplitudeEvent.FinalAfterInactivityAfterDACChoice}_${ageGroup}`,
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext?.()
    }, [onNext])

    const createFinalAfterInactivityAfterEnteringPayountAccount =
      useCallback(() => {
        trackInAmplitude(
          AmplitudeEvent.FinalAfterInactivityAfterEnteringPayoutAccount
        )
        onNext?.()
      }, [onNext])

    const createFinalAfterInactivityAfterDACInitialization = useCallback(() => {
      trackInAmplitude(
        AmplitudeEvent.FinalAfterInactivityAfterDACInitialization
      )
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: `${AmplitudeEvent.FinalAfterInactivityAfterDACInitialization}_${ageGroup}`,
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext?.()
    }, [onNext])

    const isPayoutAccountReady = usePayoutAccountValidation(isTouchedIgnored)

    const createFinalAfterInactivity = useCallback(() => {
      if (isErrorOnFinalize) {
        return
      }

      const isPayoutAccountReadyAndIsDacInitialOrDisagreed =
        isPayoutAccountReady &&
        isLastPage &&
        (dacWidgetState === 'initial' || dacWidgetState === 'disagreed')

      const isPayoutAccountReadyAndIsDacAgreed =
        isPayoutAccountReady &&
        isLastPage &&
        (dacWidgetState === 'agreed' || dacWidgetState === 'error')

      if (
        inactivityType === InactivityType.PAYOUT_ACCOUNT_INACTIVITY &&
        isPayoutAccountReadyAndIsDacInitialOrDisagreed
      ) {
        trackInAmplitude(
          AmplitudeEvent.FinalAfterInactivityAfterEnteringPayoutAccount
        )
        createFinalAfterInactivityAfterEnteringPayountAccount()
      }

      if (
        inactivityType === InactivityType.DAC_INACTIVITY &&
        isPayoutAccountReadyAndIsDacAgreed
      ) {
        trackInAmplitude(
          AmplitudeEvent.FinalAfterInactivityAfterDACInitialization
        )
        createFinalAfterInactivityAfterDACInitialization()
      }
    }, [
      inactivityType,
      isErrorOnFinalize,
      dacWidgetState,
      isPayoutAccountReady,
      createFinalAfterInactivityAfterEnteringPayountAccount,
      createFinalAfterInactivityAfterDACInitialization
    ])

    const stopTimeout = useCallback(() => {
      clearTimeout(timeoutRef.current)
      setIsTimeoutStarted(false)
    }, [setIsTimeoutStarted])

    const startTimeout = useCallback(() => {
      timeoutRef.current = window.setTimeout(() => {
        createFinalAfterInactivity()
        setIsTimeoutStarted(false)
      }, timeout)
      setIsTimeoutStarted(true)
    }, [createFinalAfterInactivity, setIsTimeoutStarted, timeout])

    const resetTimeout = useCallback(() => {
      stopTimeout()
      startTimeout()
    }, [stopTimeout, startTimeout])

    const isPayoutAccountFieldActive =
      ibanField.meta.active ||
      accountNumberField.meta.active ||
      bankCodeField.meta.active

    // active event is not caught by event listener, it needs to be done manually
    useEffect(() => {
      if (isTimeoutStarted && isPayoutAccountFieldActive) {
        logInfo('Stopping timeout for inactivity')
        stopTimeout()
      }

      if (!isTimeoutStarted && !isPayoutAccountFieldActive) {
        logInfo('Starting timeout for inactivity')
        startTimeout()
      }
    }, [
      isPayoutAccountFieldActive,
      isTimeoutStarted,
      startTimeout,
      stopTimeout,
      dacWidgetState
    ])

    useEffect(() => {
      if (hasInteracted) {
        resetTimeout()
        setHasInteracted(false)
      }
    }, [hasInteracted, setHasInteracted, resetTimeout])

    useEffect(() => {
      resetTimeout()
    }, [dacWidgetState])

    useEffect(() => {
      return () => stopTimeout()
    }, [stopTimeout])

    return null
  }
)

export default memo(CreateFinalAfterInactivity)
